import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import { useTemplates } from '../hooks/useTemplates';
import {
  Search, User, FileText, Shield, Globe, Mail, Video, MapPin, Calendar, Users, BarChart3,
  Building2, Coins, Gamepad2, Heart, Briefcase, Landmark,
  ArrowRight, CheckCircle2, Sun, Moon, Package, Code2, Zap
} from 'lucide-react';
import { BRAND } from '../../config/brand';

// Fallback pricing data if API fails
const FALLBACK_PRICING = {
  free_features: [
    { id: 'id_verification', name: 'ID Verification', description: 'Capture and verify government-issued ID documents with AI authenticity checks.' },
    { id: 'nfc_verification', name: 'NFC Verification', description: 'Cryptographically verify e-passports and e-IDs via NFC chip.' },
    { id: 'face_match', name: 'Face Match 1:1', description: 'Compare live selfie to ID photo for biometric confirmation.' },
    { id: 'passive_liveness', name: 'Passive Liveness', description: 'Frictionless AI liveness detection during selfie capture.' },
    { id: 'ip_analysis', name: 'IP Analysis', description: 'Detect VPN/proxy usage and flag high-risk locations.' },
    { id: 'reusable_kyc', name: 'Reusable KYC', description: 'Fast-track users with pre-verified Clearance credentials.' },
    { id: 'face_search', name: 'Face Search 1:N', description: 'Detect duplicate accounts by comparing against your user database.' },
  ],
  paid_features: [
    { id: 'document_ocr_premium', name: 'Document OCR (Premium)', description: 'High-accuracy extraction using AWS Textract.', price_dollars: 0.10, price_display: '$0.10' },
    { id: 'active_liveness', name: 'Active Liveness', description: 'Guided liveness check with randomized actions.', price_dollars: 0.15, price_display: '$0.15' },
    { id: 'aml_screening', name: 'AML Screening', description: 'Screen against 1000+ global sanctions and PEP lists.', price_dollars: 0.35, price_display: '$0.35' },
    { id: 'proof_of_address', name: 'Proof of Address', description: 'Capture and verify address documents.', price_dollars: 0.50, price_display: '$0.50' },
    { id: 'age_estimation', name: 'Age Estimation', description: 'Estimate age from selfie with optional ID fallback.', price_dollars: 0.10, price_display: '$0.10' },
    { id: 'database_validation', name: 'Database Validation', description: 'Validate against official government databases.', price_dollars: 0.20, price_display: '$0.20' },
  ],
  volume_discounts: [
    { min_calls: 0, max_calls: 999, discount_pct: 0, label: 'Starter' },
    { min_calls: 1000, max_calls: 9999, discount_pct: 10, label: 'Growth' },
    { min_calls: 10000, max_calls: 49999, discount_pct: 20, label: 'Scale' },
    { min_calls: 50000, max_calls: 199999, discount_pct: 30, label: 'Pro' },
    { min_calls: 200000, max_calls: null, discount_pct: 40, label: 'Enterprise' },
  ]
};

// Fetch pricing from API
function usePricing() {
  const [pricing, setPricing] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch(`${process.env.REACT_APP_API_URL || 'https://api.bytrustgate.com'}/api/v1/pricing`)
      .then(res => res.json())
      .then(data => {
        setPricing(data);
        setLoading(false);
      })
      .catch(() => {
        // Use fallback data if API fails
        setPricing(FALLBACK_PRICING);
        setLoading(false);
      });
  }, []);

  return { pricing, loading };
}

// Theme management hook (duplicated for landing page isolation)
function useTheme() {
  const [theme, setTheme] = useState(() => {
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem('portal-theme');
      if (saved) return saved;
      return window.matchMedia('(prefers-color-scheme: light)').matches ? 'light' : 'dark';
    }
    return 'dark';
  });

  useEffect(() => {
    const root = document.documentElement;
    if (theme === 'light') {
      root.classList.add('light-mode');
      root.classList.remove('dark-mode');
      root.setAttribute('data-theme', 'light');
    } else {
      root.classList.add('dark-mode');
      root.classList.remove('light-mode');
      root.setAttribute('data-theme', 'dark');
    }
    localStorage.setItem('portal-theme', theme);
  }, [theme]);

  const toggleTheme = () => setTheme(t => t === 'light' ? 'dark' : 'light');

  return { theme, toggleTheme };
}

// eslint-disable-next-line no-unused-vars
const _PRIMITIVE_ICONS = {
  'Screening': Search,
  'Face Match': User,
  'Document OCR': FileText,
  'Identity Match': Shield,
  'IP Check': Globe,
  'Email Check': Mail,
  'Liveness': Video,
  'Address Verify': MapPin,
  'Age Estimate': Calendar,
  'Face Search': Users,
  'Risk Summary': BarChart3,
};

const TEMPLATE_ICONS = {
  'fintech_kyc': Building2,
  'crypto_onramp': Coins,
  'gaming_age': Gamepad2,
  'gov_esignature': Landmark,
  'healthcare_patient': Heart,
  'hr_background': Briefcase,
};

// eslint-disable-next-line no-unused-vars
const _PRIMITIVES = [
  { name: 'Screening', description: 'AML/Sanctions/PEP check' },
  { name: 'Face Match', description: 'Compare selfie to ID photo' },
  { name: 'Document OCR', description: 'Extract data from ID documents' },
  { name: 'Identity Match', description: 'Verify identity against databases' },
  { name: 'IP Check', description: 'IP fraud and geolocation' },
  { name: 'Email Check', description: 'Email risk scoring' },
  { name: 'Liveness', description: 'Passive/active liveness detection' },
  { name: 'Address Verify', description: 'Proof of address validation' },
  { name: 'Age Estimate', description: 'Facial age estimation' },
  { name: 'Face Search', description: '1:N face search' },
  { name: 'Risk Summary', description: 'Aggregate risk scoring' },
];

const STEPS = [
  {
    number: 1,
    title: 'Install the SDK',
    description: 'npm install @getclearance/react-sdk — one command, zero config.',
    icon: Package,
  },
  {
    number: 2,
    title: 'Add 15 lines of code',
    description: 'Drop in the VerificationFlow component and handle the callback.',
    icon: Code2,
  },
  {
    number: 3,
    title: 'Go live',
    description: 'Get your API key, add billing, and start verifying users.',
    icon: CheckCircle2,
  },
];

// Fallback if API not available
const FALLBACK_TEMPLATES = [
  {
    id: 'fintech_kyc',
    name: 'Fintech KYC',
    description: 'Full KYC for banking, lending, and payments',
    step_count: 3,
    steps: ['Document OCR', 'Identity Match', 'Sanctions Screening'],
    use_cases: ['Account opening', 'Loan applications', 'Payment processing'],
    estimated_time_ms: 3000,
  },
  {
    id: 'crypto_onramp',
    name: 'Crypto Onramp',
    description: 'Regulatory-compliant crypto purchases',
    step_count: 3,
    steps: ['Face Liveness', 'Document OCR', 'AML Screening'],
    use_cases: ['Fiat-to-crypto', 'Exchange onboarding', 'Wallet creation'],
    estimated_time_ms: 2000,
  },
  {
    id: 'gaming_age',
    name: 'Gaming Age Gate',
    description: 'Age verification and payout identity',
    step_count: 2,
    steps: ['Age Estimate', 'Identity Match'],
    use_cases: ['Age-restricted content', 'Gambling compliance', 'Prize payouts'],
    estimated_time_ms: 1500,
  },
  {
    id: 'gov_esignature',
    name: 'Gov E-Signature',
    description: 'Identity verification with cryptographic attestation for PDF embedding',
    step_count: 4,
    steps: ['Document OCR', 'Face Match', 'Liveness', 'Attestation'],
    use_cases: ['Contractor agreements', 'Employee onboarding', 'Permit applications'],
    estimated_time_ms: 2500,
  },
  {
    id: 'healthcare_patient',
    name: 'Healthcare Patient ID',
    description: 'Patient identity for healthcare providers',
    step_count: 2,
    steps: ['Document OCR', 'Face Match'],
    use_cases: ['Patient registration', 'Telehealth', 'Prescription verification'],
    estimated_time_ms: 1500,
  },
  {
    id: 'hr_background',
    name: 'HR Right-to-Work',
    description: 'Employment eligibility verification',
    step_count: 3,
    steps: ['Document OCR', 'Identity Match', 'Address Verify'],
    use_cases: ['New hire onboarding', 'I-9 compliance', 'Background checks'],
    estimated_time_ms: 3500,
  },
];

export default function PortalLanding() {
  const navigate = useNavigate();
  const { loginWithRedirect, isAuthenticated } = useAuth0();
  const { data: apiTemplates } = useTemplates();
  const { theme, toggleTheme } = useTheme();
  const { pricing, loading: pricingLoading } = usePricing();
  const [monthlyVolume, setMonthlyVolume] = useState(0);

  // Auth0 handlers
  const handleLogin = () => loginWithRedirect();
  const handleSignup = () => loginWithRedirect({ authorizationParams: { screen_hint: 'signup' } });

  // Redirect to dashboard if already authenticated
  useEffect(() => {
    if (isAuthenticated) {
      navigate('/integrations', { replace: true });
    }
  }, [isAuthenticated, navigate]);

  // Use API templates if available, fallback otherwise
  const templates = apiTemplates || FALLBACK_TEMPLATES;

  // Calculate discount based on volume
  const getDiscount = (volume) => {
    if (!pricing?.volume_discounts) return { discount_pct: 0, label: 'Starter' };
    for (const tier of pricing.volume_discounts) {
      if (tier.max_calls === null || volume <= tier.max_calls) {
        if (volume >= tier.min_calls) {
          return tier;
        }
      }
    }
    return { discount_pct: 0, label: 'Starter' };
  };

  const currentDiscount = getDiscount(monthlyVolume);

  // Apply discount to price
  const getEffectivePrice = (price) => {
    if (price === null || price === 0) return price;
    const discounted = price * (1 - currentDiscount.discount_pct / 100);
    return Math.round(discounted * 100) / 100;
  };

  // Format volume number
  const formatVolume = (vol) => {
    if (vol >= 1000000) return `${(vol / 1000000).toFixed(1)}M`;
    if (vol >= 1000) return `${(vol / 1000).toFixed(0)}k`;
    return vol.toString();
  };

  const handleTryFlow = (templateId) => {
    // Trigger signup, then redirect to integrations explorer tab
    loginWithRedirect({
      authorizationParams: { screen_hint: 'signup' },
      appState: { returnTo: '/integrations' }
    });
  };

  return (
    <div className="portal-layout portal-landing">
      {/* Header */}
      <header className="portal-header">
        <div className="portal-header-left">
          <Link to="/portal" className="portal-logo">
            <div className="portal-logo-icon">TG</div>
            <span className="portal-logo-text">{BRAND.name}</span>
          </Link>
        </div>
        <div className="portal-header-right">
          <button
            className="portal-theme-toggle"
            onClick={toggleTheme}
            aria-label={`Switch to ${theme === 'light' ? 'dark' : 'light'} mode`}
          >
            {theme === 'light' ? <Moon size={18} /> : <Sun size={18} />}
          </button>
          <button onClick={handleLogin} className="portal-nav-link" style={{ background: 'none', border: 'none', cursor: 'pointer' }}>Log in</button>
          <button onClick={handleSignup} className="portal-btn portal-btn-primary portal-btn-sm">
            Sign up
            <ArrowRight size={14} />
          </button>
        </div>
      </header>

      {/* Hero */}
      <section className="portal-hero">
        <div className="portal-hero-badge">
          <Package size={14} />
          <span>npm install @getclearance/react-sdk</span>
        </div>
        <h1>
          Add identity verification in{' '}
          <span className="portal-hero-gradient">15 lines of code</span>
        </h1>
        <p className="portal-hero-subtitle">
          Drop-in React SDK for KYC verification. Document capture, liveness detection, and face matching — all handled for you.
        </p>
        <div className="portal-hero-actions">
          <button onClick={handleSignup} className="portal-btn portal-btn-primary portal-btn-lg">
            Get Started Free
            <ArrowRight size={18} />
          </button>
          <a href="https://getclearance-production.up.railway.app/docs" target="_blank" rel="noopener noreferrer" className="portal-btn portal-btn-secondary portal-btn-lg">
            <Code2 size={16} />
            API Docs
          </a>
        </div>
      </section>

      {/* SDK Code Preview */}
      <section className="portal-section portal-sdk-preview">
        <div className="portal-sdk-code-card">
          <div className="portal-sdk-code-header">
            <div className="portal-sdk-code-dots">
              <span></span><span></span><span></span>
            </div>
            <span className="portal-sdk-code-filename">VerifyPage.tsx</span>
          </div>
          <pre className="portal-sdk-code-content">{`import { VerificationFlow } from '@getclearance/react-sdk';

function VerifyPage({ token }) {
  return (
    <VerificationFlow
      sessionToken={token}
      onComplete={(result) => {
        if (result.outcome === 'approved') {
          router.push('/dashboard');
        }
      }}
    />
  );
}`}</pre>
          <div className="portal-sdk-features">
            <div className="portal-sdk-feature">
              <CheckCircle2 size={16} />
              <span>Document capture</span>
            </div>
            <div className="portal-sdk-feature">
              <CheckCircle2 size={16} />
              <span>Selfie + face match</span>
            </div>
            <div className="portal-sdk-feature">
              <CheckCircle2 size={16} />
              <span>Liveness detection</span>
            </div>
            <div className="portal-sdk-feature">
              <CheckCircle2 size={16} />
              <span>Attestation for e-signatures</span>
            </div>
          </div>
        </div>
      </section>

      {/* Industry Use Cases */}
      <section className="portal-section">
        <div className="portal-section-header">
          <h2>Built for every industry</h2>
          <p className="portal-section-subtitle">
            Click any flow to try it instantly with test data
          </p>
        </div>
        <div className="portal-grid-3">
          {templates.map((template) => {
            const IconComponent = TEMPLATE_ICONS[template.id] || Building2;
            const fallbackTemplate = FALLBACK_TEMPLATES.find(t => t.id === template.id);
            const steps = template.steps || fallbackTemplate?.steps || [];

            return (
              <div
                key={template.id}
                className="portal-template-card"
                onClick={() => handleTryFlow(template.id)}
              >
                <div className="portal-template-header">
                  <div className="portal-template-icon">
                    <IconComponent size={24} />
                  </div>
                  <span className="portal-badge portal-badge-info">{template.step_count} steps</span>
                </div>

                <h3 className="portal-template-title">{template.name}</h3>
                <p className="portal-template-desc">{template.description}</p>

                {steps.length > 0 && (
                  <div className="portal-template-steps">
                    {steps.slice(0, 3).map((step, idx) => (
                      <div key={idx} className="portal-template-step">
                        <ArrowRight size={12} />
                        <span>{step}</span>
                      </div>
                    ))}
                  </div>
                )}

                <div className="portal-template-tags">
                  {template.use_cases?.slice(0, 3).map((useCase) => (
                    <span key={useCase} className="portal-tag">
                      {useCase}
                    </span>
                  ))}
                </div>

                <div className="portal-template-footer">
                  <span className="portal-template-meta">
                    ~{(template.estimated_time_ms / 1000).toFixed(1)}s
                  </span>
                  <button
                    className="portal-btn portal-btn-primary portal-btn-sm"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleTryFlow(template.id);
                    }}
                  >
                    Try Flow
                    <ArrowRight size={14} />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* Pricing Section */}
      <section className="portal-section portal-section-dark" id="pricing">
        <div className="portal-section-header">
          <span className="portal-section-badge">TRANSPARENT PRICING</span>
          <h2>Pay only for what you use</h2>
          <p className="portal-section-subtitle">
            Free Core KYC included. Premium features at transparent per-call pricing.
          </p>
        </div>

        {/* Volume Slider */}
        <div className="portal-volume-slider">
          <div className="portal-volume-header">
            <span className="portal-volume-label">Monthly volume</span>
            <span className="portal-volume-value">{formatVolume(monthlyVolume)}</span>
          </div>
          <input
            type="range"
            min="0"
            max="200000"
            step="1000"
            value={monthlyVolume}
            onChange={(e) => setMonthlyVolume(Number(e.target.value))}
            className="portal-slider"
          />
          <div className="portal-volume-marks">
            <span>0</span>
            <span>50k</span>
            <span>100k</span>
            <span>150k</span>
            <span>200k+</span>
          </div>
          {currentDiscount.discount_pct > 0 && (
            <div className="portal-discount-badge">
              <Zap size={14} />
              <span>{currentDiscount.discount_pct}% volume discount ({currentDiscount.label})</span>
            </div>
          )}
        </div>

        {/* Pricing Table */}
        <div className="portal-pricing-table">
          {pricingLoading ? (
            /* Loading skeleton while pricing data loads */
            <div className="portal-pricing-loading">
              <div className="portal-pricing-skeleton">
                <div className="portal-pricing-skeleton-row" />
                <div className="portal-pricing-skeleton-row" />
                <div className="portal-pricing-skeleton-row" />
                <div className="portal-pricing-skeleton-row" />
              </div>
            </div>
          ) : (
          <>
          {/* Free Features */}
          <div className="portal-pricing-section">
            <h3 className="portal-pricing-section-title">
              <CheckCircle2 size={20} className="portal-pricing-free-icon" />
              Free Forever
            </h3>
            <div className="portal-pricing-list">
              {(pricing?.free_features || []).map((feature) => (
                <div key={feature.id} className="portal-pricing-row">
                  <div className="portal-pricing-feature">
                    <span className="portal-pricing-name">{feature.name}</span>
                    <span className="portal-pricing-desc">{feature.description}</span>
                  </div>
                  <span className="portal-pricing-price portal-pricing-free">Free</span>
                </div>
              ))}
            </div>
          </div>

          {/* Paid Features */}
          <div className="portal-pricing-section">
            <h3 className="portal-pricing-section-title">Premium Add-ons</h3>
            <div className="portal-pricing-list">
              {(pricing?.paid_features || []).map((feature) => {
                const effectivePrice = getEffectivePrice(feature.price_dollars);
                const hasDiscount = currentDiscount.discount_pct > 0 && feature.price_dollars !== null;

                return (
                  <div key={feature.id} className="portal-pricing-row">
                    <div className="portal-pricing-feature">
                      <span className="portal-pricing-name">{feature.name}</span>
                      <span className="portal-pricing-desc">{feature.description}</span>
                    </div>
                    <div className="portal-pricing-price-container">
                      {hasDiscount && (
                        <span className="portal-pricing-original">${feature.price_dollars.toFixed(2)}</span>
                      )}
                      <span className={`portal-pricing-price ${hasDiscount ? 'portal-pricing-discounted' : ''}`}>
                        {feature.price_display === 'Variable' ? 'Variable' :
                          effectivePrice !== null ? `$${effectivePrice.toFixed(2)}` : 'Variable'}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Volume Discounts Info */}
          <div className="portal-volume-tiers">
            <h4>Volume Discounts</h4>
            <div className="portal-volume-tiers-grid">
              {(pricing?.volume_discounts || []).map((tier, idx) => (
                <div
                  key={idx}
                  className={`portal-volume-tier ${monthlyVolume >= tier.min_calls && (tier.max_calls === null || monthlyVolume <= tier.max_calls) ? 'active' : ''}`}
                >
                  <span className="portal-volume-tier-range">
                    {formatVolume(tier.min_calls)}{tier.max_calls ? `-${formatVolume(tier.max_calls)}` : '+'}
                  </span>
                  <span className="portal-volume-tier-discount">
                    {tier.discount_pct > 0 ? `${tier.discount_pct}% off` : 'Base price'}
                  </span>
                </div>
              ))}
            </div>
          </div>
          </>
          )}
        </div>

        <div className="portal-pricing-footer">
          <p>No setup fees. No monthly minimums. You only pay for what you use.</p>
          <button onClick={handleSignup} className="portal-btn portal-btn-primary portal-btn-lg">
            Get Started Free
            <ArrowRight size={18} />
          </button>
        </div>
      </section>

      {/* How It Works */}
      <section className="portal-section">
        <div className="portal-section-header">
          <h2>Three steps to production</h2>
        </div>
        <div className="portal-grid-3">
          {STEPS.map((step) => {
            const IconComponent = step.icon;
            return (
              <div key={step.number} className="portal-card portal-step-card hover-lift">
                <div className="portal-step-number">
                  <IconComponent size={20} />
                </div>
                <h3>{step.title}</h3>
                <p>{step.description}</p>
              </div>
            );
          })}
        </div>
      </section>

      {/* CTA Banner */}
      <section className="portal-section portal-cta-section">
        <div className="portal-cta-content">
          <h2>Start building for free</h2>
          <p>No setup fees. No contracts. No monthly minimums.</p>
          <button onClick={handleSignup} className="portal-btn portal-btn-primary portal-btn-lg">
            Get Started
            <ArrowRight size={18} />
          </button>
        </div>
      </section>

      {/* Footer */}
      <footer className="portal-footer">
        <div className="portal-footer-content">
          <div className="portal-footer-brand">
            <div className="portal-logo">
              <div className="portal-logo-icon">TG</div>
              <span className="portal-logo-text">{BRAND.name}</span>
            </div>
            <p>Enterprise-grade identity verification for modern applications.</p>
          </div>
          <div className="portal-footer-links">
            <div className="portal-footer-col">
              <h4>Product</h4>
              <Link to="/integrations">Dashboard</Link>
              <a href="https://getclearance-production.up.railway.app/docs" target="_blank" rel="noopener noreferrer">API Docs</a>
              <a href="https://getclearance-production.up.railway.app/docs#section/Pricing" target="_blank" rel="noopener noreferrer">Pricing</a>
            </div>
            <div className="portal-footer-col">
              <h4>SDKs</h4>
              <a href="https://github.com/autorevai/getclearance-react" target="_blank" rel="noopener noreferrer">React SDK</a>
              <a href="https://github.com/autorevai/getclearance-python" target="_blank" rel="noopener noreferrer">Python SDK</a>
            </div>
            <div className="portal-footer-col">
              <h4>Company</h4>
              <span className="portal-footer-link">About</span>
              <span className="portal-footer-link">Contact</span>
              <span className="portal-footer-link">Security</span>
            </div>
          </div>
        </div>
        <div className="portal-footer-bottom">
          <span>{BRAND.copyright(new Date().getFullYear())}</span>
        </div>
      </footer>
    </div>
  );
}
